import { CurrencyPipe, Location, NgIf } from '@angular/common';
import { Component, ViewChild, computed, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { DialogService, FeedbackService, NavigationService, TitleService } from '@app/core/services';
import { MaterialModule } from '@app/shared';
import { ButtonBarComponent, TitleComponent } from '@app/shared/components';
import { BaseDetailsComponent } from '@app/shared/components/base-details.component';
import { BankEntity } from '@app/shared/models/bank';
import { BankService } from '@app/shared/services';
import { ReadOnly } from '@app/utils/claims';
import { BankAddressesComponent } from './bank-addresses/bank-addresses.component';
import { BankCashAccountsComponent } from './bank-cash-accounts/bank-cash-accounts.component';
import { BankCheckConfigComponent } from './bank-check-config/bank-check-config.component';
import { BankDetailTabs } from './bank-detail-tabs.constant';
import { BankProfileComponent } from './bank-profile/bank-profile.component';
import { BankReconSetupComponent } from './bank-recon-setup/bank-recon-setup.component';
import { BankSitesComponent } from './bank-sites/bank-sites.component';
import { IBankTabChangeEvent } from './bank-tab-change-event.interface';
import { PositivePaySetupComponent } from './positive-pay-setup/positive-pay-setup.component';

@Component({
	selector: 'sb-banking-detail',
	standalone: true,
	imports: [
    RouterLink,
    TitleComponent,
    CurrencyPipe,
    NgIf,
    MaterialModule,
    BankProfileComponent,
    BankAddressesComponent,
    BankCheckConfigComponent,
    BankCashAccountsComponent,
    BankSitesComponent,
    BankReconSetupComponent,
    PositivePaySetupComponent,
    ButtonBarComponent
],
	templateUrl: './bank-detail.component.html',
	styleUrl: './bank-detail.component.less',
})
export class BankingDetailComponent extends BaseDetailsComponent<BankEntity, BankService> {
	@ReadOnly() isReadOnly = true;

	private tabClickHandlerInitialized = false;

	private _tabGroup: MatTabGroup;
	@ViewChild(MatTabGroup)
	set tabGroup(value: MatTabGroup) {
		// The tab group is loaded conditionally, so use a setter to respond when it's available.
		this._tabGroup = value;
		if (value && !this.tabClickHandlerInitialized) {
			this.setupTabClickHandler();
		}
	}
	get tabGroup(): MatTabGroup {
		return this._tabGroup;
	}

	// Cast the entity from Partial<BankEntity> to a BankEntity for compatibility with child components.
	get bankEntity(): BankEntity {
		return this.entity as BankEntity;
	}
	set bankEntity(value: BankEntity) {
		this.entity = value;
	}

	entityName: string = 'Bank';
	listingPagePath: string = '/lsb/system-settings/system-setup/banking';
	pageTitleProperty: keyof BankEntity = 'BankName';
	pageTitleSuffix: string = 'Banking Info';
	protected entityLoaded = false;
	protected selectedTabIndex: number = 0;
	protected tabs = BankDetailTabs;

	// These are required by the base details component but not used here because form submission is delegated
	// to child components within the tabs.
	options: FormlyFormOptions;
	fields: FormlyFieldConfig[];

	private readonly changedTabIndex = signal<number | null>(null);
	private changedTabEvent: IBankTabChangeEvent | null = null;
	protected tabHasChanges = computed(() => this.changedTabIndex() !== null);
	canDeactivate = (): boolean => !this.tabHasChanges();

	constructor(
		protected readonly route: ActivatedRoute,
		protected readonly location: Location,
		protected readonly router: Router,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly navigationService: NavigationService,
		protected readonly titleService: TitleService,
		protected readonly entityService: BankService,
		private readonly dialogService: DialogService,
	) {
		super(route, location, router, dialog, feedbackService, navigationService, titleService);
	}

	private setupTabClickHandler(): void {
		// Save the click handler for later
		const handleTabClick = this.tabGroup._handleClick;

		// Replace the click handler with our custom function
		this.tabGroup._handleClick = (tab, header, index) => {
			if (this.tabHasChanges()) {
				this.onUnsavedChanges();
			} else {
				// If the tab *should* be changed, call the default click handler
				handleTabClick.apply(this.tabGroup, [tab, header, index]);
			}
		};

		this.tabClickHandlerInitialized = true;
	}

	private onUnsavedChanges(): void {
		const message = `Changes have not been saved for the ${this.changedTabEvent?.tabName ?? 'current'} tab.`
			+ ` <br>Please save or discard your changes before switching tabs.`;
		this.dialogService.open({
			title: 'Changes Not Saved',
			message: message,
			okText: 'OK',
			showCancel: false,
		});
	}

	protected onEntityLoaded(): void {
		this.entityLoaded = true;
	}

	protected canDelete(): boolean {
		const canDelete = this.entity.CanDelete && !this.isReadOnly;
		return canDelete;
	}

	protected onTabContentChange(event: IBankTabChangeEvent): void {
		if (event) {
			this.changedTabEvent = event;
			this.changedTabIndex.set(this.selectedTabIndex);
		} else {
			this.changedTabEvent = null;
			this.changedTabIndex.set(null);
		}
	}

	protected onCurrentBalanceChange(newBalance: number): void {
		this.entity.CurrentBalance = newBalance;
	}

	protected saveChanges(): void {
		if (this.changedTabEvent) {
			this.feedbackService.clearAll();
			const tabName = this.changedTabEvent?.tabName ?? this.entityName;
			this.changedTabEvent.saveChanges().subscribe((success) => {
				if (success) {
					this.changedTabIndex.set(null);
					this.feedbackService.success('updated', tabName);
				} else if (!this.changedTabEvent.isValid()) {
					this.feedbackService.error(`${tabName} contains invalid entries. Please correct and try again.`);
				} else {
					this.feedbackService.error(`Failed to save ${tabName} changes. Please try again.`);
				}
			});
		}
	}

	protected cancelChanges(): void {
		if (this.changedTabEvent) {
			this.changedTabEvent.cancelChanges();
			this.changedTabIndex.set(null);
			this.feedbackService.clearAll();
		}
	}
}
